'use client';

import { useContext, useState } from 'react';

import ProductPageInformation from 'components/pdp/product-page-information';
import MediaGallery from 'components/pdp/media-gallery';
import styled from 'styled-components';
import Url from 'lib/siteurls';
import PdpLocale from 'locale/en/pdp';
import { mqXXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import { Hero } from 'components/ds01/hero-styled';
import { ProductsContext } from 'context-providers/products-provider';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import ClinicalTrials from 'components/pdp/clinical-trials';
import DSStrains from 'components/pdp/ds01/ds-strains';
import { richTextToHtml } from 'components/pdp/modules/helpers';

import { useFeatureFlags } from 'context-providers/feature-flag-provider';
import { supFootnoteMarkersHtml } from 'lib/utils/footnote-markers';

const Srp0Container = styled.div`
  max-width: 100vw;
  width: 100%;
  align-items: center;
  display: block;
  padding: 1.5rem 1rem;

  @media ${mqLargeMin} {
    padding: 1rem;
  }

  @media ${mqXXLargeMin} {
    max-width: 1440px;
  }
`;

const StickyContainer = styled.div`
  @media ${mqLargeMin} {
    position: sticky;
    top: 1rem;
    width: 27rem;
    align-self: flex-start;
  }
`;

const BuyboxHero = styled(Hero)`
  margin-bottom: 0.5rem;

  @media ${mqLargeMin} {
    margin-bottom: 4rem;
  }
`;

const SrpHero = styled.div<{ $paddingBottom?: string }>`
  @media ${mqLargeMin} {
    ${({ $paddingBottom }) =>
      $paddingBottom && `padding-bottom: ${$paddingBottom};`}
  }
`;

const Buybox = ({
  productInfo,
  useCartContext,
  selectedSku,
  selectedSkus,
  siteBuy,
  buyBox,
  bundleProducts,
  strainsModule,
  benefitIcons,
  yotpoAverageScore,
  yotpoTotalReviews,
  ds01SalePrice,
  // "Formulated to°:" line above the benefit bullets.
  formulatedTo,
  // Cobiotics PDPs: unstarred Benefits heading, Active Ingredients heading, capsule-count price line.
  isCobiotics = false,
}) => {
  const { getProduct, shopifyProducts } = useContext(ProductsContext);

  const [clinicalTrialsOpen, setClinicalTrialsOpen] = useState(false);
  const [ingredientsOpen, setIngredientsOpen] = useState(false);

  if (
    !selectedSku &&
    !(Array.isArray(selectedSkus) && selectedSkus.length > 0)
  ) {
    return null;
  }

  const { audienceFlags } = useFeatureFlags();
  const isQuizAudience = audienceFlags.quizAudience;

  const benefits = buyBox?.benefits ?? [];
  const ingredients = buyBox?.ingredients ?? [];

  const selectedProduct = selectedSku
    ? getProduct(selectedSku)
    : getProduct(selectedSkus[0]);
  const bundleProduct = selectedSkus ? getProduct(selectedSkus[1]) : null;

  const allVariants = Object.values(shopifyProducts).filter((variant) => {
    return variant.productID === productInfo?.id;
  });

  const variantImages = allVariants.reduce((images, variant) => {
    if (variant?.image?.url) {
      images.push(variant?.image?.url);
    }
    return images;
  }, []);

  const mediaNodes = (productInfo?.media?.nodes || []).filter((image) => {
    return !variantImages.includes(image?.previewImage?.url);
  });

  return (
    <BuyboxHero $adjustRight={true}>
      <Srp0Container className='container' id='srp0-container'>
        <SrpHero
          className='srpHero'
          $paddingBottom={isQuizAudience ? '0 !important' : null}
        >
          <MediaGallery media={mediaNodes} />
          <StickyContainer>
            <ProductPageInformation
              hidePromo={true}
              accordionItems={[
                {
                  title: isCobiotics ? 'Benefits' : 'Benefits*',
                  content:
                    (formulatedTo
                      ? `<p>${supFootnoteMarkersHtml(formulatedTo)}</p>`
                      : '') +
                    '<ul>' +
                    benefits.reduce((content, overview) => {
                      return `${content}<li>${overview}</li>`;
                    }, '') +
                    '</ul>',
                  contentCTA:
                    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC
                      ? 'View Clinical Trials'
                      : null,
                  contentCTACallback:
                    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC
                      ? () => {
                          setClinicalTrialsOpen(true);
                        }
                      : null,
                  accordionEvent: {
                    buttonName: 'FAQ',
                    question: 'Benefits',
                    location: 'PDP',
                  },
                },
                {
                  title: isCobiotics ? 'Active Ingredients' : 'Ingredients',
                  content:
                    '<ul>' +
                    ingredients.reduce((content, ingredient) => {
                      return `${content}<li>${ingredient}</li>`;
                    }, '') +
                    '</ul>',
                  contentCTA:
                    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC
                      ? 'View Strains + Ingredients'
                      : null,
                  contentCTACallback:
                    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC
                      ? () => {
                          setIngredientsOpen(true);
                        }
                      : null,
                  accordionEvent: {
                    buttonName: 'FAQ',
                    question: 'Ingredients',
                    location: 'PDP',
                  },
                },
              ]}
              selectedProduct={selectedProduct}
              purchaseUrl={Url.cart}
              useCartContext={useCartContext}
              selectedSku={selectedSku}
              bundleProduct={bundleProduct}
              productInfo={productInfo}
              siteBuy={siteBuy}
              bundleProducts={bundleProducts}
              citations={buyBox?.citations}
              yotpoAverageScore={yotpoAverageScore}
              yotpoTotalReviews={yotpoTotalReviews}
              ds01SalePrice={ds01SalePrice}
              guaranteeHtml={
                isCobiotics ? PdpLocale.pdp_guarantee_capsules : undefined
              }
            />
          </StickyContainer>
        </SrpHero>
      </Srp0Container>
      {productInfo && selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC && (
        <>
          <ClinicalTrials
            productInfo={productInfo}
            isOpen={clinicalTrialsOpen}
            setIsOpen={setClinicalTrialsOpen}
            contents={
              richTextToHtml(buyBox?.clinicalTrials, {
                newLineToBreak: true,
              }) ?? ''
            }
          >
            {false}
          </ClinicalTrials>
          <ClinicalTrials
            productInfo={productInfo}
            isOpen={ingredientsOpen}
            setIsOpen={setIngredientsOpen}
            contents=''
          >
            <DSStrains strainsModule={strainsModule} icons={benefitIcons} />
          </ClinicalTrials>
        </>
      )}
    </BuyboxHero>
  );
};

export default Buybox;
